import requests from "../requests";
import { Dispatch } from "redux";

export enum AlertsConstants {
  ALERTS_REQUEST = "ALERTS_REQUEST",
  ALERTS_SUCCESS = "ALERTS_SUCCESS",
  ALERTS_FAILURE = "ALERTS_FAILURE",

  ALERT_CREATE_REQUEST = "ALERT_CREATE_REQUEST",
  ALERT_CREATE_SUCCESS = "ALERT_CREATE_SUCCESS",
  ALERT_CREATE_FAILURE = "ALERT_CREATE_FAILURE",

  ALERT_UPDATE_REQUEST = "ALERT_UPDATE_REQUEST",
  ALERT_UPDATE_SUCCESS = "ALERT_UPDATE_SUCCESS",
  ALERT_UPDATE_FAILURE = "ALERT_UPDATE_FAILURE",

  ALERT_DELETE_REQUEST = "ALERT_DELETE_REQUEST",
  ALERT_DELETE_SUCCESS = "ALERT_DELETE_SUCCESS",
  ALERT_DELETE_FAILURE = "ALERT_DELETE_FAILURE",

  FEED_REQUEST = "FEED_REQUEST",
  FEED_SUCCESS = "FEED_SUCCESS",
  FEED_FAILURE = "FEED_FAILURE",

  PATTERN_THRESHOLDS_REQUEST = "PATTERN_THRESHOLDS_REQUEST",
  PATTERN_THRESHOLDS_SUCCESS = "PATTERN_THRESHOLDS_SUCCESS",
  PATTERN_THRESHOLDS_FAILURE = "PATTERN_THRESHOLDS_FAILURE",
}

const alertsService = {
  async getAlerts(): Promise<AlertsResponse> {
    const resp = await requests.get('/api/alerts');
    // Normalize: an empty/malformed body (e.g. a proxy/cache returning no JSON)
    // must not leave `alerts` undefined — that crashes every alerts.length consumer.
    const data = resp.data as Partial<AlertsResponse> | undefined;
    return { alerts: Array.isArray(data?.alerts) ? data.alerts : [] };
  },
  async createAlert(data: Omit<UserAlert, 'id' | 'created_at' | 'updated_at'>): Promise<UserAlert> {
    const resp = await requests.post('/api/alerts', data);
    return resp.data;
  },
  async updateAlert(id: string, data: Partial<UserAlert>): Promise<UserAlert> {
    const resp = await requests.put(`/api/alerts/${id}`, data);
    return resp.data;
  },
  async deleteAlert(id: string): Promise<void> {
    await requests.delete(`/api/alerts/${id}`);
  },
  async getFeed(lookbackDays = 7, limit = 200): Promise<FeedResponse> {
    const params = new URLSearchParams({ lookback_days: lookbackDays.toString(), limit: limit.toString() });
    const resp = await requests.get(`/api/feed?${params}`);
    // Normalize: an empty/malformed body must not leave `feed` undefined —
    // groupByDate() iterates it and would crash the whole Feed page (blank).
    const data = resp.data as Partial<FeedResponse> | undefined;
    return {
      feed: Array.isArray(data?.feed) ? data.feed : [],
      total: data?.total ?? 0,
      lookback_days: data?.lookback_days ?? lookbackDays,
      alert_count: data?.alert_count ?? 0,
    };
  },
  async getPatternThresholds(): Promise<PatternThresholdsResponse> {
    const resp = await requests.get('/api/patterns/performance');
    return resp.data;
  },
};

export function getAlerts() {
  return async (dispatch: Dispatch<AlertsAction>): Promise<void> => {
    dispatch({ type: AlertsConstants.ALERTS_REQUEST });
    try {
      dispatch({ type: AlertsConstants.ALERTS_SUCCESS, payload: await alertsService.getAlerts() });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: AlertsConstants.ALERTS_FAILURE, error: (error as any)?.response?.data?.error || 'Failed to load alerts' });
    }
  };
}

export function createAlert(data: Omit<UserAlert, 'id' | 'created_at' | 'updated_at'>) {
  return async (dispatch: Dispatch<AlertsAction>): Promise<void> => {
    dispatch({ type: AlertsConstants.ALERT_CREATE_REQUEST });
    try {
      dispatch({ type: AlertsConstants.ALERT_CREATE_SUCCESS, payload: await alertsService.createAlert(data) });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: AlertsConstants.ALERT_CREATE_FAILURE, error: (error as any)?.response?.data?.error || 'Failed to create alert' });
    }
  };
}

export function updateAlert(id: string, data: Partial<UserAlert>) {
  return async (dispatch: Dispatch<AlertsAction>): Promise<void> => {
    dispatch({ type: AlertsConstants.ALERT_UPDATE_REQUEST });
    try {
      dispatch({ type: AlertsConstants.ALERT_UPDATE_SUCCESS, payload: await alertsService.updateAlert(id, data) });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: AlertsConstants.ALERT_UPDATE_FAILURE, error: (error as any)?.response?.data?.error || 'Failed to update alert' });
    }
  };
}

export function deleteAlert(id: string) {
  return async (dispatch: Dispatch<AlertsAction>): Promise<void> => {
    dispatch({ type: AlertsConstants.ALERT_DELETE_REQUEST });
    try {
      await alertsService.deleteAlert(id);
      dispatch({ type: AlertsConstants.ALERT_DELETE_SUCCESS, payload: { id } });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: AlertsConstants.ALERT_DELETE_FAILURE, error: (error as any)?.response?.data?.error || 'Failed to delete alert' });
    }
  };
}

export function getFeed(lookbackDays = 7, limit = 200) {
  return async (dispatch: Dispatch<AlertsAction>): Promise<void> => {
    dispatch({ type: AlertsConstants.FEED_REQUEST });
    try {
      dispatch({ type: AlertsConstants.FEED_SUCCESS, payload: await alertsService.getFeed(lookbackDays, limit) });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: AlertsConstants.FEED_FAILURE, error: (error as any)?.response?.data?.error || 'Failed to load feed' });
    }
  };
}

export function getPatternThresholds() {
  return async (dispatch: Dispatch<AlertsAction>): Promise<void> => {
    dispatch({ type: AlertsConstants.PATTERN_THRESHOLDS_REQUEST });
    try {
      dispatch({ type: AlertsConstants.PATTERN_THRESHOLDS_SUCCESS, payload: await alertsService.getPatternThresholds() });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch({ type: AlertsConstants.PATTERN_THRESHOLDS_FAILURE, error: (error as any)?.response?.data?.error || 'Failed to load thresholds' });
    }
  };
}
